import { Component, h } from 'preact'
import axios from 'axios'
import FeaturedTopicView from './FeaturedTopicView'
import FeaturedProductUpdateView from './FeaturedProductUpdateView'
import { EventListItemView } from '../event/EventListOverview'
import Button from '../../control-components/ui-components/Button'
import {UserData} from '../event/EventAttendees'

export interface Author {
  id: number,
  url: string,
  name: string,
  avatar: string
}

export interface Post {
  id: number,
  author: Author,
  content: string,
  url: string,
  creationDate: string,
  relativeCreationDate: string
}

interface IdeationStatus {
  name: string,
  bgColor: string,
  color: string
}

export interface FeaturedTopic {
  id: number,
  title: string,
  description: string,
  url: string,
  type: number,
  featuredImage: string,
  label: string,
  replyCount: number,
  views: number,
  post: Post,
  customClass: string,
  likes: number,
  hasCurrentUserLiked: boolean,
  ideationStatus?: IdeationStatus,
  contentType: string
}

export interface FeaturedProductUpdate {
  id: number,
  title: string,
  url: string,
  type: number,
  featuredImage: string,
  label: string,
  replyCount: number,
  customClass: string,
  contentType: 'productUpdate',
  content: string,
  author: Author,
  createdAt: string
}

export interface FeaturedEvent {
  title: string,
  url: string,
  type?: string,
  startsAt: number,
  endsAt: number,
  image?: string,
  location?: string,
  contentType: string,
  isLoggedInUserAttendee: boolean,
  attendees?: Array<UserData>,
  attendeeCount?: number
}

interface Props {
  title: string,
  topicsFullWidth: number,
  topicIds: number[]|{id: number, type: string}[],
  buttonLabel?: string,
  buttonUrl?: string,
  featuredTopics: (FeaturedTopic|FeaturedEvent|FeaturedProductUpdate)[]
}

interface State {
  featuredTopics: (FeaturedTopic|FeaturedEvent|FeaturedProductUpdate)[]
}

export default class FeaturedTopicsWidget extends Component<Props, State> {
  constructor (props: Props) {
    super(props)

    this.state = {
      featuredTopics: props.featuredTopics || []
    }

    this.addLikeHandler = this.addLikeHandler.bind(this)
  }

  async addLikeHandler (index: number) {
    if (typeof window === 'undefined') return
    if (!window.inSidedData.user.userid) {
      const event = new window.CustomEvent('GuestUserAction')
      return window.dispatchEvent(event)
    }

    const newFeaturedTopics = [...this.state.featuredTopics]
    const topic = newFeaturedTopics[index] as FeaturedTopic
    let { post, id, likes, hasCurrentUserLiked: liked } = topic

    topic.likes = liked ? --likes : ++likes
    topic.hasCurrentUserLiked = !liked

    this.setState({
      featuredTopics: newFeaturedTopics
    })

    const url = `/member/like?like=${topic.hasCurrentUserLiked}&tid=${id}`

    const newData = {
      liked: topic.hasCurrentUserLiked,
      postid: post.id
    }

    return await axios.post(url, { ...newData })
  }

  renderEvent ({title, url, startsAt, endsAt, image, type, location, attendeeCount, attendees, isLoggedInUserAttendee}: FeaturedEvent, customClass: string, index: number, topicsFullWidth: number) {
    return <article
      className={`${image && 'featured-topic--featured-image'} ${customClass}`}>
      <EventListItemView
        title={title}
        startAt={startsAt}
        endAt={endsAt}
        destinationUrl={url}
        image={image}
        type={type}
        location={location}
        isFullWidth={index < topicsFullWidth}
        isFeatured
        attendees={attendees}
        attendeeCount={attendeeCount}
        isLoggedInUserAttendee={isLoggedInUserAttendee}
      />
    </article>
  }

  renderProductUpdate (topic: FeaturedProductUpdate, index: number, topicsFullWidth: number, customClass: string) {
    const { id, content, label, title, featuredImage, url, replyCount, author, createdAt } = topic
    return (
      <FeaturedProductUpdateView
        id={id}
        content={content}
        label={label || ''}
        title={title}
        featuredImage={featuredImage}
        author={author}
        replyCount={replyCount}
        url={url}
        fullWidth={index < topicsFullWidth}
        customClass={customClass}
        index={index}
        createdAt={createdAt}
      />
    );
  }

  renderTopic ({ likes, type, title, featuredImage, label, replyCount, views, url, post, ideationStatus, hasCurrentUserLiked }: FeaturedTopic, index: number, topicsFullWidth: number, customClass: string) {
    return (
      <FeaturedTopicView
        title={title}
        featuredImage={featuredImage}
        label={label}
        replyCount={replyCount}
        views={views}
        url={url}
        post={post}
        fullWidth={index < topicsFullWidth}
        customClass={customClass}
        type={type}
        likes={likes}
        addLikeHandler={this.addLikeHandler}
        index={index}
        ideationStatus={ideationStatus}
        hasCurrentUserLiked={hasCurrentUserLiked}
      />
    )
  }

  render (
    { title, topicsFullWidth, topicIds, buttonLabel = '', buttonUrl = '' }: Props,
    { featuredTopics }: State
  ) {
    if (!topicIds || !topicIds.length) {
      return null
    }
    const externalLink = buttonUrl && (typeof window !== 'undefined' && buttonUrl.indexOf(window.location.hostname) < 0)
    const isDeviceMobile: boolean = typeof window !== 'undefined' && window.inSidedData?.device === 'mobile'

    return (
      <section class='featured-topics qa-featured-topics-container sitewidth'>
        {title &&
          <h3 class='widget__heading featured-topics__heading'>{title}</h3>
        }
        <div class={`featured-topics__list ${featuredTopics.length === 1 ? 'featured-topics__list--no-slider' : ''}`}>
          {
            featuredTopics.map((featured, index) => {
              let customClass = ''

              if (!isDeviceMobile && index >= topicsFullWidth && ((featuredTopics.length - topicsFullWidth === 2) || (featuredTopics.length - topicsFullWidth === 4))) {
                customClass = 'featured-topic--half-width'
              }

              if (!isDeviceMobile && index >= topicsFullWidth && (featuredTopics.length - topicsFullWidth === 1)) {
                customClass = 'featured-topic--full-width'
              }
              if ('startsAt' in featured) {
                customClass = 'featured-topic ' + customClass
                customClass = (index < topicsFullWidth) ? 'featured-topic--full-width ' + customClass : customClass

                return this.renderEvent(featured, customClass, index, topicsFullWidth)
              }
              if ('content' in featured) {
                return this.renderProductUpdate(featured, index, topicsFullWidth, customClass)
              }

              return this.renderTopic(featured, index, topicsFullWidth, customClass)
            }
          )}
        </div>
        {buttonLabel && buttonUrl &&
          <a
            class='featured-topics__show-more-button'
            href={buttonUrl}
            target={`${externalLink  ? '_blank' : '_self'}`}
            rel={`${externalLink ? 'noreferrer nofollow noopener' : ''}`}
          >
            <Button
              title={buttonLabel}
            />
          </a>
        }
      </section>
    )
  }
}
